import { Card, mergeClasses } from '@fluentui/react-components';

import { MemberGoalCompletedResult } from 'src/models/health-goals/MemberGoalCompletedResult';
import { MemberGoalResult } from 'src/models/health-goals/MemberGoalResult';
import {
  getCalculatedUnit,
  getCalendarRange,
  getGoalRepeatabilityRateText,
  getMeasurementUnit,
  getProgressValue,
  getSubtitle,
  getCompletionLabel,
  isCompleted,
  isEnded,
  getIcon,
} from 'src/utils/healthGoalsUtils';
import { HealthGoalType } from 'src/utils/enums';
import HealthGoalBodyForStepsExercise, {
  HealthGoalBodyData,
} from 'src/components/molecules/health-goal-body-for-steps-exercise/HealthGoalBodyForStepsExercise';
import { ERROR_MESSAGES } from 'src/utils/constants';
import HealthGoalHeader, { HealthGoalHeaderData } from '../../molecules/health-goal-header/HealthGoalHeader';
import { useHealthGoalCardStyles } from './HealthGoalCard.styles';

interface HealthGoalCardProps {
  pinnedHealthGoal: MemberGoalResult | MemberGoalCompletedResult
  className?: string
}

const HealthGoalCard: React.FC<HealthGoalCardProps> = ({ pinnedHealthGoal, className }) => {
  const styles = useHealthGoalCardStyles();
  const { phrTitle, healthGoalType, biometricTestId } = pinnedHealthGoal;
  const isCompletedStatus = isCompleted(pinnedHealthGoal);
  const isEndedStatus = !isCompletedStatus && isEnded(pinnedHealthGoal);
  const healthGoalHeader: HealthGoalHeaderData = {
    subTitle: getSubtitle(pinnedHealthGoal),
    title: phrTitle,
    completionLabel: getCompletionLabel(pinnedHealthGoal),
    isCompleted: isCompletedStatus,
    isEnded: isEndedStatus,
    icon: getIcon(healthGoalType, biometricTestId),
  };

  const renderStepsOrExerciseBody = (activeGoal: MemberGoalResult): React.ReactNode => {
    const {
      rate,
      calculatedType,
      calculatedTarget,
      currentMeasurementCount,
      healthGoalWindowStart,
      healthGoalWindowEnd,
      isRepeatable,
    } = activeGoal;
    const healthGoalBody: HealthGoalBodyData = {
      calendarRange: getCalendarRange(rate, healthGoalWindowStart, healthGoalWindowEnd),
      calculatedUnit: getCalculatedUnit(calculatedType),
      currentMeasurementCount: currentMeasurementCount.toLocaleString(),
      measurementUnit: getMeasurementUnit(rate, healthGoalType),
      progressValue: getProgressValue(rate, currentMeasurementCount, calculatedTarget),
      goalRepeatabilityRate: isRepeatable ? getGoalRepeatabilityRateText(rate) : null,
    };

    return <HealthGoalBodyForStepsExercise healthGoalBody={healthGoalBody} />;
  };

  // TODO: this function's called when pinnedHealthGoal is active
  const getHealthGoalBody = (): React.ReactNode => {
    switch (healthGoalType) {
      case HealthGoalType.StepsGoal:
      case HealthGoalType.ExerciseGoal:
        return renderStepsOrExerciseBody(pinnedHealthGoal as MemberGoalResult);
      case HealthGoalType.BiometricScreening:
      case HealthGoalType.BiometricTest:
      case HealthGoalType.BiometricTestCurrent:
      case HealthGoalType.PreventiveTests:
      case HealthGoalType.TrackingGoal:
        return null;
      default:
        throw new Error(ERROR_MESSAGES.invalidHealthGoalType(healthGoalType));
    }
  };

  return (
    <Card className={mergeClasses('health-goal-card-container', styles.card, className)} data-testid="health-goal-card">
      <HealthGoalHeader healthGoalHeader={healthGoalHeader} />
      {getHealthGoalBody()}
    </Card>
  );
};

export default HealthGoalCard;
